<template>
  <section class="pdph" v-if="visible">
    <div class="pdph-card">
      <header class="pdph-head">
        <h3 class="pdph-title">
          <i class="fa fa-money"></i>
          <template v-if="isPaidInFull">Paid in full</template>
          <template v-else>Make a payment</template>
        </h3>
      </header>

      <!-- Paid-so-far / remaining -->
      <div class="pdph-balance">
        <div class="pdph-balance-row">
          <span class="pdph-balance-label">Paid so far</span>
          <span class="pdph-balance-val">{{ formatMoney(paidTotalNum) }}</span>
        </div>
        <div class="pdph-balance-row pdph-balance-remaining">
          <span class="pdph-balance-label">Remaining</span>
          <span class="pdph-balance-val">{{ formatMoney(remainingNum) }}</span>
        </div>
      </div>

      <!-- Payment history -->
      <ul v-if="payments && payments.length" class="pdph-history">
        <li v-for="p in payments" :key="p.paymentID" class="pdph-history-row">
          <i class="fa fa-check-circle"></i>
          <span class="pdph-history-amt">{{ formatMoney(p.amount) }}</span>
          <span class="pdph-history-meta">
            {{ methodLabel(p) }}<template v-if="paymentDate(p)"> · {{ paymentDate(p) }}</template>
          </span>
        </li>
      </ul>

      <!-- Sign-first lock: a balance remains but the plan isn't signed yet. -->
      <div v-if="!isPaidInFull && !signed" class="pdph-locked">
        <i class="fa fa-lock"></i>
        <span>Sign the treatment plan to make a payment.</span>
      </div>

      <!-- Make a payment (enabled once the plan is signed) -->
      <div v-else-if="!isPaidInFull && signed" class="pdph-body">
        <label class="pdph-amt-label">Amount to pay</label>
        <div class="pdph-amt-row">
          <span class="pdph-amt-prefix">$</span>
          <input
            v-model="amountInput"
            class="pdph-amt-input"
            type="number"
            min="1"
            step="0.01"
            :max="payCeiling"
            inputmode="decimal"
            :disabled="phase === 'creating'"
          />
        </div>
        <p class="pdph-amt-hint" :class="{ 'pdph-amt-hint-err': !!amountError }">
          {{ amountError || ('You can pay any amount up to ' + formatMoney(payCeiling) + '.') }}
        </p>
        <p v-if="creditAmount > 0" class="pdph-amt-credit">
          <i class="fa fa-info-circle"></i>
          Includes {{ formatMoney(creditAmount) }} credit on your account.
        </p>

        <div class="pdph-actions">
          <button
            class="pdph-btn pdph-btn-primary"
            :disabled="phase === 'creating' || !validAmount"
            @click="onPayNow"
          >
            <i v-if="phase === 'creating'" class="fa fa-circle-o-notch fa-spin"></i>
            <i v-else class="fa fa-credit-card"></i>
            Pay {{ formatMoney(validAmount || 0) }} now
          </button>
        </div>

        <p class="pdph-fineprint">
          You'll pay securely via Moolah. Your card details never touch this page.
        </p>

        <div v-if="error" class="pdph-error">
          <i class="fa fa-exclamation-circle"></i>
          {{ error }}
        </div>
      </div>

      <!-- Paid in full -->
      <p v-else class="pdph-paidfull">
        <i class="fa fa-check-circle"></i>
        This plan is fully paid. Thank you!
      </p>
    </div>
  </section>
</template>

<script>
// moment-timezone so payment dates render in the practice's timezone (the
// patient may be in a different zone, and PaidAt comes back as a UTC ISO).
import moment from 'moment-timezone';

/**
 * At-home payment panel on the public patient view (TxPatientView.vue), below
 * the signature card. "Pay toward balance" ledger (Slice 2): shows the paid-so-far
 * + remaining and every prior payment, and lets the patient pay any amount up to
 * the remaining balance — repeatedly, until paid in full.
 *
 * Card-only at home (cash/check/financing are chairside concepts). Pay-now creates
 * a balance_payments row (intent='prepayment') and hands off to the existing public
 * /process-payment/{PaymentID} page (Moolah Collect.js). On return to the plan link
 * the payload re-queries balance_payments, so the new payment shows in the history
 * and remaining drops. Multiple payments per plan already work at the data layer
 * (each is its own row), so "pay again" needs no special handling.
 */
export default {
  name: 'PatientDepositPanel',
  props: {
    siteID: { type: String, required: true },
    patient: { type: Object, default: () => null }, // { patNum, fName, lName, WirelessPhone, Email }
    treatPlanNum: { type: [Number, String], default: null },
    // Office's suggested amount (chairside "Pay today") — seeds the input, capped at remaining.
    suggestedAmount: { type: [Number, String], default: 0 },
    signed: { type: Boolean, default: false },
    // Ledger from the patient-view payload.
    payments: { type: Array, default: () => [] }, // [{ paymentID, amount, paidAt, method, optionTitle, cardBrand }]
    paidTotal: { type: [Number, String], default: 0 },
    remaining: { type: [Number, String], default: 0 },
    // Ceiling for how much can be paid in one go — the full plan total. Amounts
    // above `remaining` but at/under this land the difference as account credit;
    // above it is rejected. Defaults to 0 (degrades to the remaining-only cap).
    maxPayable: { type: [Number, String], default: 0 },
    // Practice IANA timezone (from the patient-view payload) — payment dates are
    // shown in the office's local time, since the patient may be elsewhere.
    timezone: { type: String, default: '' },
  },

  data() {
    return {
      phase: 'idle', // 'idle' | 'creating'
      error: null,
      amountInput: '',
    };
  },

  computed: {
    remainingNum() {
      const n = parseFloat(this.remaining);
      return Number.isFinite(n) && n > 0 ? Math.round(n * 100) / 100 : 0;
    },
    paidTotalNum() {
      const n = parseFloat(this.paidTotal);
      return Number.isFinite(n) && n > 0 ? Math.round(n * 100) / 100 : 0;
    },
    // How much the patient may pay in one transaction: the full plan total when
    // the parent passes it, floored at `remaining` so the ceiling can never dip
    // below what's owed. Falls back to `remaining` when maxPayable is missing/0,
    // degrading to the prior remaining-only behavior.
    payCeiling() {
      return Math.max(this.round2(this.maxPayable), this.remainingNum);
    },
    // Overpayment (amount above what's owed) that will land as account credit.
    creditAmount() {
      return this.validAmount > this.remainingNum
        ? this.round2(this.validAmount - this.remainingNum)
        : 0;
    },
    isPaidInFull() {
      return this.remainingNum <= 0 && (this.paidTotalNum > 0 || (this.payments && this.payments.length > 0));
    },
    visible() {
      // Show whenever there's a balance to pay OR payment history — signed or
      // not. When unsigned we render a "sign first" lock instead of the pay
      // controls (see template), so the payment area is discoverable but gated.
      // Provider gating stays server-side (createMoolahPaymentRecord rejects if
      // the office hasn't set up Moolah).
      return this.remainingNum > 0 || (this.payments && this.payments.length > 0);
    },
    validAmount() {
      const n = parseFloat(this.amountInput);
      if (!Number.isFinite(n) || n <= 0) return 0;
      const rounded = Math.round(n * 100) / 100;
      return rounded <= this.payCeiling ? rounded : 0;
    },
    amountError() {
      const n = parseFloat(this.amountInput);
      if (this.amountInput === '' || !Number.isFinite(n)) return '';
      if (n <= 0) return 'Enter an amount greater than $0.';
      if (Math.round(n * 100) / 100 > this.payCeiling) {
        return `The most you can pay is ${this.formatMoney(this.payCeiling)}.`;
      }
      return '';
    },
  },

  watch: {
    remaining: { immediate: true, handler() { this.seedAmount(); } },
  },

  methods: {
    formatMoney(n) {
      const v = parseFloat(n) || 0;
      return '$' + v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    round2(n) {
      return Math.round((parseFloat(n) || 0) * 100) / 100;
    },
    seedAmount() {
      // Default the input to the office's suggested amount, capped at remaining;
      // fall back to the full remaining when no suggestion is set.
      const rem = this.remainingNum;
      if (rem <= 0) { this.amountInput = ''; return; }
      const sugg = parseFloat(this.suggestedAmount) || 0;
      const def = sugg > 0 ? Math.min(sugg, rem) : rem;
      this.amountInput = (Math.round(def * 100) / 100).toFixed(2);
    },
    methodLabel(p) {
      if (!p) return 'Payment';
      if (p.method === 'cash') return 'Cash';
      if (p.method === 'check') return 'Check';
      if (p.method === 'option') return p.optionTitle ? `Via ${p.optionTitle}` : 'Financing';
      if (p.cardBrand) return p.cardBrand;
      return 'Card';
    },
    paymentDate(p) {
      const iso = p && p.paidAt;
      if (!iso) return '';
      // PaidAt is UTC (…Z); convert to the practice timezone when we have a valid
      // one, else fall back to the patient's local interpretation of the UTC time.
      const tz = this.timezone && moment.tz.zone(this.timezone) ? this.timezone : null;
      const m = tz ? moment.utc(iso).tz(tz) : moment(iso);
      return m.isValid() ? m.format('MMM D, YYYY') : '';
    },

    async onPayNow() {
      if (this.phase === 'creating') return;
      const amt = this.validAmount;
      if (!amt) return;
      this.error = null;
      this.phase = 'creating';
      try {
        const created = await this.$store.dispatch('moolah/createMoolahPaymentRecord', {
          siteID: this.siteID,
          patNum: this.patient && this.patient.patNum,
          balance: amt,
          patientName: this.patient && `${this.patient.fName || ''} ${this.patient.lName || ''}`.trim(),
          patientPhone: this.patient && (this.patient.WirelessPhone || this.patient.phone),
          intent: 'prepayment',
          treatPlanNum: this.treatPlanNum,
        });
        const paymentID = created && created.paymentID;
        if (!paymentID) throw new Error('Could not start the payment. Please try again.');

        // Hand off to the public payment page (Moolah Collect.js). On success the
        // patient returns to this plan link; the re-queried ledger shows the new
        // payment + reduced remaining.
        const origin = window.location.origin;
        window.location.href = `${origin}/process-payment/${paymentID}`;
      } catch (err) {
        console.error('[PatientDepositPanel] pay-now failed:', err);
        this.error = (err && err.response && err.response.data && err.response.data.message)
          || (err && err.message) || 'Could not start the payment. Please try again.';
        this.phase = 'idle';
      }
    },
  },
};
</script>

<style scoped>
.pdph {
  margin: 28px 0 0;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, system-ui, sans-serif;
}
.pdph-card {
  background: #fff;
  border: 1px solid #e4e7ec;
  border-radius: 12px;
  padding: 22px;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.05);
}
.pdph-head { margin-bottom: 14px; }
.pdph-title {
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: #0d5f54;
  display: flex;
  align-items: center;
  gap: 8px;
}
.pdph-title .fa { font-size: 15px; }

/* Balance summary */
.pdph-balance {
  border: 1px solid #e4e7ec;
  border-radius: 10px;
  padding: 12px 14px;
  margin-bottom: 14px;
  background: #f8fafc;
}
.pdph-balance-row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  font-size: 14px;
  color: #475569;
}
.pdph-balance-row + .pdph-balance-row { margin-top: 6px; }
.pdph-balance-val { font-weight: 600; color: #1e293b; font-variant-numeric: tabular-nums; }
.pdph-balance-remaining .pdph-balance-label { font-weight: 700; color: #0d5f54; }
.pdph-balance-remaining .pdph-balance-val {
  font-size: 18px;
  font-weight: 700;
  color: #0d5f54;
}

/* Payment history */
.pdph-history {
  list-style: none;
  margin: 0 0 16px;
  padding: 0;
}
.pdph-history-row {
  display: flex;
  align-items: baseline;
  gap: 8px;
  padding: 7px 0;
  border-bottom: 1px solid #f1f3f6;
  font-size: 13.5px;
}
.pdph-history-row:last-child { border-bottom: none; }
.pdph-history-row .fa { color: #047857; font-size: 13px; }
.pdph-history-amt { font-weight: 700; color: #047857; font-variant-numeric: tabular-nums; }
.pdph-history-meta { color: #64748b; font-size: 12.5px; }

/* Amount input */
.pdph-amt-label {
  display: block;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #475569;
  margin-bottom: 6px;
}
.pdph-amt-row {
  display: flex;
  align-items: center;
  border: 1px solid #cbd5e1;
  border-radius: 9px;
  padding: 0 12px;
  background: #fff;
}
.pdph-amt-row:focus-within { border-color: #0d5f54; box-shadow: 0 0 0 3px rgba(13, 95, 84, 0.12); }
.pdph-amt-prefix { font-size: 18px; font-weight: 600; color: #475569; }
.pdph-amt-input {
  flex: 1;
  border: none;
  outline: none;
  padding: 12px 8px;
  font-size: 18px;
  font-weight: 600;
  color: #1e293b;
  background: transparent;
  font-variant-numeric: tabular-nums;
}
.pdph-amt-hint {
  margin: 6px 0 14px;
  font-size: 12px;
  color: #64748b;
}
.pdph-amt-hint-err { color: #b91c1c; }
/* Overpay info note — distinct from the error (teal/info, not red). */
.pdph-amt-credit {
  display: flex;
  align-items: center;
  gap: 6px;
  margin: -8px 0 14px;
  font-size: 12px;
  font-weight: 500;
  color: #0d5f54;
}
.pdph-amt-credit .fa { font-size: 12px; color: #0d5f54; }

.pdph-actions {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 12px;
}
.pdph-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 14px 18px;
  border-radius: 9px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid #e4e7ec;
  background: #fff;
  color: #1e293b;
  width: 100%;
}
.pdph-btn:disabled { opacity: 0.55; cursor: not-allowed; }
.pdph-btn-primary {
  background: #0d5f54;
  border-color: #0d5f54;
  color: #fff;
}
.pdph-btn-primary:hover:not(:disabled) { background: #094840; }

.pdph-fineprint {
  margin: 0;
  font-size: 11.5px;
  color: #64748b;
  text-align: center;
  line-height: 1.5;
}

.pdph-error {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
  padding: 10px 12px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #b91c1c;
  border-radius: 8px;
  font-size: 12px;
}

.pdph-paidfull {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: #047857;
}
/* Sign-first lock — payment is discoverable but gated until the plan is signed. */
.pdph-locked {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 14px;
  border: 1px dashed #cbd5e1;
  border-radius: 9px;
  background: #f8fafc;
  color: #64748b;
  font-size: 13px;
  font-weight: 500;
}
.pdph-locked .fa { color: #94a3b8; }

@media print {
  .pdph { display: none !important; }
}
</style>
